import React, { useMemo, useState } from "react";
import { Check, Sparkles, Link2, Info, Layers3 } from "lucide-react";
import { toast } from "sonner";
import { computeCompare } from "../../lib/lookup";
import { formatPrice } from "../../lib/matrix";
import { PartsTitle, PriceBlock } from "./PathCard";
import ShareMenu from "./ShareMenu";

const SHOW = 8;

export default function ComparePanel({ dataset, plans, addons, groups, priceMode, pricing, pinned, onOpenFeature, cardRef, onCardData }) {
  const [groupFilter, setGroupFilter] = useState("all");
  const symbol = pricing?.symbol || "$";
  const result = useMemo(
    () => computeCompare({ rows: dataset.rows, plans, addons, names: pinned, priceMode }),
    [dataset, plans, addons, pinned, priceMode]
  );

  const groupLabel = (id) => groups.find((g) => g.id === id)?.tableLabel || "";
  const combos = (result?.combos || []).filter((c) => groupFilter === "all" || c.groupId === groupFilter);
  const usedGroups = groups.filter((g) => (result?.combos || []).some((c) => c.groupId === g.id));

  React.useEffect(() => {
    if (!result) return;
    onCardData({
      title: `Cheapest bundle for ${result.names.length} features`,
      subtitle: `Single license bundle that covers: ${result.names.join(" · ")}`,
      tags: [],
      features: [],
      items: result.combos.slice(0, 5).map((c) => ({ key: c.key, parts: c.parts, total: c.total, estimated: c.estimated, coverage: c.coverage, badge: groupLabel(c.groupId) })),
      note: "Bundle = one base license plus the suite / add-ons needed for every pinned feature.",
    });
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyLink = async () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("lookup");
    url.searchParams.set("compare", pinned.join("|"));
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Compare link copied");
    } catch {
      toast(url.toString());
    }
  };

  if (!result || result.features.length < 2) {
    return (
      <div className="lookup-empty" data-testid="compare-empty">
        <Info size={16} /> Pin at least two features to find one bundle that covers them all.
      </div>
    );
  }

  return (
    <div className="lookup-results compare" data-testid="compare-results">
      <div className="lookup-feature">
        <div>
          <h3 data-testid="compare-title">
            <Layers3 size={16} aria-hidden="true" /> One bundle for {result.names.length} features
          </h3>
          <div className="lookup-context">Cheapest base license plus the suite / add-ons needed to cover every pinned feature.</div>
        </div>
        <div className="lookup-actions">
          <button type="button" className="tb-btn sm" onClick={copyLink} data-testid="compare-copy-link">
            <Link2 size={13} /> Copy link
          </button>
          <ShareMenu cardRef={cardRef} title={`bundle-${result.names.length}-features`} disabled={!result.combos.length} />
        </div>
      </div>

      {result.missing.length > 0 && (
        <div className="lookup-empty warn">
          <Info size={14} /> Not in dataset: {result.missing.join(", ")}
        </div>
      )}

      <div className="lookup-summary">
        <span className="chip">
          <strong>{result.combos.length}</strong> bundles
        </span>
        {result.cheapest && (
          <span className="chip cheapest" data-testid="compare-cheapest">
            <Sparkles size={12} aria-hidden="true" /> Cheapest: {result.cheapest.parts.map((p) => (p.kind === "plan" ? p.name : p.label)).join(" + ")} at{" "}
            {formatPrice(result.cheapest.total, symbol, result.cheapest.estimated)}
          </span>
        )}
      </div>

      {usedGroups.length > 1 && (
        <div className="pills lookup-groups" role="group" aria-label="Filter by license family">
          <button type="button" className="pill" aria-pressed={groupFilter === "all"} onClick={() => setGroupFilter("all")}>
            All
          </button>
          {usedGroups.map((g) => (
            <button key={g.id} type="button" className="pill" aria-pressed={groupFilter === g.id} onClick={() => setGroupFilter(g.id)}>
              {g.tableLabel}
            </button>
          ))}
        </div>
      )}

      <ol className="path-list">
        {combos.slice(0, SHOW).map((c, idx) => {
          const isCheapest = result.cheapest && c.key === result.cheapest.key;
          return (
            <li key={c.key} className={`path-card bundle ${isCheapest ? "cheapest" : ""}`} data-testid="compare-bundle">
              <div className="path-rank">{idx + 1}</div>
              <div className="path-main">
                <PartsTitle parts={c.parts} />
                <div className="path-meta">
                  <span className="mini-chip">{groupLabel(c.groupId)}</span>
                  <span className="mini-chip level">{c.parts.length === 1 ? "single license" : `${c.parts.length - 1} add-on${c.parts.length > 2 ? "s" : ""}`}</span>
                  {c.estimated && <span className="mini-chip est">{"\u2248"} estimated</span>}
                  {isCheapest && (
                    <span className="mini-chip cheapest">
                      <Sparkles size={10} aria-hidden="true" /> cheapest
                    </span>
                  )}
                </div>
                <ul className="coverage" data-testid="compare-coverage">
                  {c.coverage.map((cov) => (
                    <li key={cov.name}>
                      <Check size={12} aria-hidden="true" />
                      <button type="button" className="link-btn" onClick={() => onOpenFeature(cov.name)} title="Open single-feature lookup">
                        {cov.name}
                      </button>
                      <span className="via">via {cov.via}</span>
                    </li>
                  ))}
                </ul>
                {c.total === null && <div className="path-note warn">An add-on price is missing - total not available</div>}
              </div>
              <PriceBlock parts={c.parts} total={c.total} estimated={c.estimated} symbol={symbol} />
            </li>
          );
        })}
      </ol>
      {combos.length === 0 && <div className="lookup-empty">No bundle in this license family covers all pinned features.</div>}
      {combos.length > SHOW && <p className="lookup-foot">Showing the {SHOW} cheapest of {combos.length} bundles.</p>}
    </div>
  );
}
